import React, { useEffect } from "react";

import {
  Autocomplete,
  Button,
  Box,
  TextField,
  Alert,
  Typography,
  Paper,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import AddIcon from "@mui/icons-material/Add";

import { fetchProjects } from "../../../Dashboard/services/projects.service";

interface Props {
  handleOnChange: (event: React.SyntheticEvent, data: any) => void;
  hasError?: boolean;
  value?: { label: string; id: string } | null;
  onCreateProjectClick?: () => void;
  projectsRefreshTrigger?: number;
  autoSelectProjectId?: string | null;
  autoSelectProjectName?: string | null;
  onAutoSelectProjectApplied?: () => void;
}

export default function SelectProject({
  handleOnChange,
  hasError = false,
  value,
  onCreateProjectClick,
  projectsRefreshTrigger = 0,
  autoSelectProjectId = null,
  autoSelectProjectName = null,
  onAutoSelectProjectApplied,
}: Props) {
  const {
    data: response,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["projects"],
    queryFn: () => fetchProjects(),
  });

  const options =
    response?.projects?.map((project: any) => {
      return { label: project.name, id: project.id };
    }) || [];

  // Auto-select the project if there's only one available
  useEffect(() => {
    if (options.length === 1 && !value) {
      const singleProject = options[0];
      // Simulate the change event to auto-select
      handleOnChange({} as React.SyntheticEvent, singleProject);
    }
  }, [options, value, handleOnChange]);

  useEffect(() => {
    if (projectsRefreshTrigger > 0) {
      refetch();
    }
  }, [projectsRefreshTrigger, refetch]);

  useEffect(() => {
    if (!autoSelectProjectId && !autoSelectProjectName) return;

    const createdProjectOption = options.find((option) => {
      if (autoSelectProjectId) return option.id === autoSelectProjectId;
      return option.label === autoSelectProjectName;
    });

    if (!createdProjectOption) return;

    if (value?.id !== createdProjectOption.id) {
      handleOnChange({} as React.SyntheticEvent, createdProjectOption);
    }

    onAutoSelectProjectApplied?.();
  }, [
    autoSelectProjectId,
    autoSelectProjectName,
    options,
    value?.id,
    handleOnChange,
    onAutoSelectProjectApplied,
  ]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[50]
              : theme.palette.grey[900],
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              alignItems: "flex-start",
              flex: 1,
            }}
          >
            <FolderOutlinedIcon
              sx={{
                fontSize: 22,
                color: "#2563eb",
                mt: 0.125,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.55,
                fontSize: "0.875rem",
              }}
            >
              Choose which project this episode belongs to.
            </Typography>
          </Box>
          {onCreateProjectClick && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={onCreateProjectClick}
              sx={{
                flexShrink: 0,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: "divider",
                color: "text.primary",
                px: 1.25,
                "&:hover": {
                  borderColor: "#2563eb",
                  color: "#2563eb",
                  backgroundColor: "rgba(37,99,235,0.04)",
                },
              }}
            >
              Create project
            </Button>
          )}
        </Box>

        <Autocomplete
          disablePortal
          clearOnEscape
          options={options || []}
          loading={isLoading}
          disabled={isLoading || !!error}
          value={value}
          onOpen={() => {
            // Refetch projects when dropdown is opened to get latest data
            refetch();
          }}
          sx={{
            width: "100%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: 15,
              backgroundColor: "background.paper",
              "&:hover fieldset": { borderColor: "#2563eb" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2563eb",
                borderWidth: 2,
              },
            },
            "& .MuiInputLabel-root": {
              fontSize: 15,
              fontWeight: 500,
              color: "text.secondary",
              "&.Mui-focused": {
                color: "#2563eb",
                fontWeight: 600,
              },
            },
            "& .MuiAutocomplete-option": {
              fontSize: 15,
              py: 1.25,
              px: 2,
              borderRadius: 1,
              transition: "background 0.15s",
              '&[aria-selected="true"]': {
                background: "rgba(37, 99, 235, 0.08)",
                color: "#2563eb",
              },
              "&:hover": {
                background: "action.hover",
              },
            },
            "& .MuiAutocomplete-listbox": {
              borderRadius: "8px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              py: 0.5,
              background: "background.paper",
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Project"
              placeholder={
                isLoading ? "Loading projects..." : "Choose a project..."
              }
            />
          )}
          onChange={handleOnChange}
          isOptionEqualToValue={(option, selectedValue) =>
            option.id === selectedValue.id
          }
        />

        {error && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: "8px",
                "& .MuiAlert-message": {
                  fontSize: 14,
                  fontWeight: 500,
                },
              }}
            >
              Failed to load projects. Please try again.
            </Alert>
          </Box>
        )}

        {hasError && (
          <Box sx={{ mt: 2 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: "8px",
                "& .MuiAlert-message": {
                  fontSize: 14,
                  fontWeight: 500,
                },
              }}
            >
              Please select a project before uploading the episode.
            </Alert>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
